import { onDocumentReady } from '@theme/utilities';

const STORAGE_KEY = 'tree2mydoor:favorites';
const MAX_FAVORITES = 25;
const ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const ADD_TO_CART_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"/><path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width)" d="M14.608 12.563v5m2.5-2.5h-5"/></svg>';
const CHECKMARK_ICON =
  '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-checkmark"><path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.0902 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * Escape HTML entities for safe insertion
 * @param {string | undefined | null} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/[&<>"']/g, (character) => ESCAPE_MAP[character] || character);
}

/**
 * Normalise CDN image URLs and enforce a width parameter
 * @param {string | undefined | null} src
 * @param {number} width
 * @returns {string}
 */
function normaliseImage(src, width = 800) {
  if (!src) return '';
  let normalised = src.startsWith('//') ? `https:${src}` : src;
  if (normalised.startsWith('/')) {
    normalised = `${window.location.origin}${normalised}`;
  }
  if (!normalised.includes('width=')) {
    const separator = normalised.includes('?') ? '&' : '?';
    normalised = `${normalised}${separator}width=${width}`;
  }
  return normalised;
}

/**
 * Convert a cents value into a formatted price string
 * @param {number | undefined | null} cents
 * @param {string} fallback
 * @returns {string}
 */
function formatPrice(cents, fallback = '') {
  if (Number.isFinite(cents)) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const moneyFormat = window.Shopify.money_format || window.theme?.moneyFormat || '{{amount_with_currency}}';
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    try {
      const currencySetting = window.Shopify?.currency;
      const currency = typeof currencySetting === 'object' && currencySetting && 'active' in currencySetting
        ? currencySetting.active
        : (typeof currencySetting === 'string' ? currencySetting : 'GBP');
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (_error) {
      return (cents / 100).toFixed(2);
    }
  }

  return fallback;
}

class FavoritesManager {
  constructor() {
    /** @type {FavoritesItem[]} */
    this.items = this.load();
    /** @type {Set<HTMLButtonElement>} */
    this.buttons = new Set();
    /** @type {HTMLElement | null} */
    this.listContainer = null;
    /** @type {string} */
    this.sectionId = '';
    /** @type {string} */
    this.quantityDefault = '1';
    /** @type {number} */
    this.renderRequestId = 0;
    /** @type {boolean} */
    this.pageStylesLoaded = false;
    this.templates = {
      add: 'Add {{ product_title }} to favourites',
      remove: 'Remove {{ product_title }} from favourites',
      loading: 'Loading your favourites…',
      view: 'View product',
      send: 'Send gift',
      soldOut: 'Sold out',
      added: 'Added',
    };

    this.handleClick = this.handleClick.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);

    document.addEventListener('click', this.handleClick);
    document.addEventListener('shopify:section:load', this.handleSectionLoad);
    document.addEventListener('cart:update', this.handleCartUpdate);

    this.observeMutations();
    this.scan(document);
    this.updateCount();
    this.initList();
  }

  /** @returns {FavoritesItem[]} */
  load() {
    if (typeof window.localStorage === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const normalised = parsed
        .filter((item) => item && typeof item.handle === 'string')
        .map((item) => ({
          handle: item.handle,
          productId: item.productId ?? '',
          title: item.title ?? '',
          url: item.url ?? '',
          image: item.image ?? '',
          price: item.price ?? '',
          variantId: item.variantId ? String(item.variantId) : '',
          available: item.available === true || item.available === 'true',
          variantImage: item.variantImage ?? '',
        }));

      if (normalised.length > MAX_FAVORITES) {
        return normalised.slice(-MAX_FAVORITES);
      }

      return normalised;
    } catch (_error) {
      return [];
    }
  }

  save() {
    if (typeof window.localStorage === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
    } catch (_error) {
      /* no-op if storage is unavailable */
    }
  }

  handleSectionLoad(event) {
    if (event && event.target instanceof HTMLElement) {
      this.scan(event.target);
      this.renderFavoritesList();
    }
  }

  observeMutations() {
    if (typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            if (node.matches('[data-favorite-toggle]')) {
              this.registerButton(node);
            }
            this.scan(node);
          }
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Scan the DOM for favourite toggle buttons
   * @param {ParentNode} root
   */
  scan(root) {
    if (!(root instanceof HTMLElement || root instanceof Document)) return;
    const buttons = root.querySelectorAll('[data-favorite-toggle]');
    buttons.forEach((button) => this.registerButton(/** @type {HTMLButtonElement} */ (button)));
  }

  /**
   * Register a button and sync its state
   * @param {HTMLButtonElement} button
   */
  registerButton(button) {
    if (button.dataset.favoriteInitialized === 'true') return;
    button.dataset.favoriteInitialized = 'true';
    this.buttons.add(button);
    this.updateButton(button);
  }

  /** @param {MouseEvent} event */
  handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-favorite-toggle]');
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();

    const product = this.getProductData(button);
    const isFavorite = this.toggleFavorite(product, button);

    if (isFavorite) {
      this.animateButton(button);
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @returns {FavoritesItem}
   */
  getProductData(button) {
    const handle = button.dataset.productHandle || '';
    const productId = button.dataset.productId || '';
    const url = button.dataset.productUrl || `/products/${handle}`;
    const title = button.dataset.productTitle || handle;
    const price = button.dataset.productPrice || '';
    const image = button.dataset.productImage || '';

    return { handle, productId, title, url, price, image };
  }

  /**
   * Toggle favourite state for a product
   * @param {FavoritesItem} product
   */
  toggleFavorite(product, button) {
    if (!product.handle) return;
    const index = this.items.findIndex((item) => item.handle === product.handle);

    if (index >= 0) {
      this.items.splice(index, 1);
      if (button) {
        this.updateButton(button);
      }
      this.refreshButtons();

      const removalAnimation = this.animateRemoval(product.handle);
      removalAnimation.finally(() => {
        this.afterChange();
      });

      return false;
    }

    if (this.items.length >= MAX_FAVORITES) {
      this.items.shift();
    }

    this.items.push({ ...product });
    this.afterChange();

    return true;
  }

  afterChange() {
    this.save();
    this.refreshButtons();
    this.updateCount();
    void this.renderFavoritesList();
  }

  refreshButtons() {
    for (const button of Array.from(this.buttons)) {
      if (!button.isConnected) {
        this.buttons.delete(button);
        continue;
      }
      this.updateButton(button);
    }
  }

  /**
   * @param {HTMLButtonElement} button
   */
  updateButton(button) {
    const handle = button.dataset.productHandle;
    if (!handle) return;

    const isActive = this.items.some((item) => item.handle === handle);
    const addLabel = button.dataset.labelAdd || this.templates.add;
    const removeLabel = button.dataset.labelRemove || this.templates.remove;
    const label = isActive ? removeLabel : addLabel;

    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    button.setAttribute('aria-label', label);

    const text = button.querySelector('.favorite-toggle__label');
    if (text) text.textContent = label;
  }

  /**
   * @param {string} handle
   * @returns {boolean}
   */
  isFavorite(handle) {
    if (!handle) return false;
    return this.items.some((item) => item.handle === handle);
  }

  /**
   * @param {HTMLButtonElement} button
   */
  animateButton(button) {
    const icon = button.querySelector('.favorite-toggle__icon');
    if (!(icon instanceof HTMLElement)) return;

    const animationClasses = ['animate__animated', 'animate__rubberBand'];
    const start = () => {
      icon.classList.add(...animationClasses);
      icon.addEventListener('animationend', () => {
        icon.classList.remove(...animationClasses);
      }, { once: true });
    };

    icon.classList.remove(...animationClasses);
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(start);
      });
    } else {
      start();
    }
  }

  /**
   * Apply a subtle removal animation to all matching favourite cards.
   * @param {string} handle
   * @returns {Promise<void>}
   */
  animateRemoval(handle) {
    return new Promise((resolve) => {
      if (!handle) {
        resolve();
        return;
      }

      const safeHandle =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? CSS.escape(handle)
          : handle.replace(/"/g, '\\"');

      const cards = Array.from(
        document.querySelectorAll(`[data-favorite-item="${safeHandle}"]`)
      );

      if (!cards.length) {
        resolve();
        return;
      }

      let remaining = cards.length;

      const finish = () => {
        remaining -= 1;
        if (remaining <= 0) {
          resolve();
        }
      };

      cards.forEach((card) => {
        if (!(card instanceof HTMLElement)) {
          finish();
          return;
        }

        card.classList.add('is-removing');

        let settled = false;

        const cleanup = () => {
          if (settled) return;
          settled = true;
          finish();
        };

        const fallback = window.setTimeout(cleanup, 400);

        const handleCleanup = () => {
          window.clearTimeout(fallback);
          cleanup();
        };

        card.addEventListener('animationend', handleCleanup, { once: true });
        card.addEventListener('transitionend', handleCleanup, { once: true });
      });
    });
  }

  initList() {
    this.listContainer = document.querySelector('[data-favorites-container]');
    if (!this.listContainer) return;

    this.ensurePageStyles();

    const dataset = this.listContainer.dataset;
    if (dataset.sectionId) this.sectionId = dataset.sectionId;
    if (dataset.quantityDefault) this.quantityDefault = dataset.quantityDefault;
    if (dataset.labelAddTemplate) this.templates.add = dataset.labelAddTemplate;
    if (dataset.labelRemoveTemplate) this.templates.remove = dataset.labelRemoveTemplate;
    if (dataset.loadingLabel) this.templates.loading = dataset.loadingLabel;
    if (dataset.viewLabel) {
      this.templates.view = dataset.viewLabel;
      if (!dataset.sendLabel) {
        this.templates.send = dataset.viewLabel;
      }
    }
    if (dataset.sendLabel) this.templates.send = dataset.sendLabel;
    if (dataset.soldOutLabel) this.templates.soldOut = dataset.soldOutLabel;
    if (dataset.addedLabel) this.templates.added = dataset.addedLabel;

    this.renderFavoritesList();
  }

  updateCount() {
    const count = this.items.length;

    document.querySelectorAll('[data-favorite-count]').forEach((bubble) => {
      if (!(bubble instanceof HTMLElement)) return;
      bubble.hidden = count === 0;
      bubble.textContent = count > 9 ? '9+' : String(count);
    });

    document.querySelectorAll('[data-favorites-link]').forEach((link) => {
      if (!(link instanceof HTMLElement)) return;
      const bubble = link.querySelector('[data-favorite-count]');
      const countTemplate = bubble?.dataset.countLabel || '';
      const baseLabel = link.dataset.originalLabel || link.getAttribute('aria-label') || '';

      if (count > 0 && countTemplate) {
        link.setAttribute('aria-label', countTemplate.replace('{{ count }}', String(count)));
      } else if (baseLabel) {
        link.setAttribute('aria-label', baseLabel);
      }
    });
  }

  async renderFavoritesList() {
    if (!this.listContainer) return;

    this.ensurePageStyles();

    const grid = this.listContainer.querySelector('[data-favorites-grid]');
    const empty = this.listContainer.querySelector('[data-favorites-empty]');
    const loading = this.listContainer.querySelector('[data-favorites-loading]');

    if (!(grid instanceof HTMLElement)) return;

    const favorites = this.items.map((item) => ({ ...item }));

    if (!favorites.length) {
      grid.innerHTML = '';
      if (loading instanceof HTMLElement) loading.hidden = true;
      if (empty instanceof HTMLElement) empty.hidden = false;
      return;
    }

    if (empty instanceof HTMLElement) empty.hidden = true;
    if (loading instanceof HTMLElement) loading.hidden = false;

    const currentRequest = ++this.renderRequestId;

    const enriched = await Promise.all(
      favorites.map((item) => this.enrichProduct(item).catch(() => item))
    );

    if (currentRequest !== this.renderRequestId) return;

    const markup = enriched.map((product) => this.createCardMarkup(product)).join('');
    grid.innerHTML = markup;
    this.scan(grid);

    if (loading instanceof HTMLElement) loading.hidden = true;

    this.items = enriched.map((item) => ({
      handle: item.handle,
      productId: item.productId ?? '',
      title: item.title ?? '',
      url: item.url ?? '',
      image: item.image ?? '',
      price: item.price ?? '',
      variantId: item.variantId ? String(item.variantId) : '',
      available: item.available === true,
      variantImage: item.variantImage ?? '',
    }));
    this.save();
    this.refreshButtons();
  }

  /**
   * @param {FavoritesItem} item
   * @returns {Promise<FavoritesItem>}
   */
  async enrichProduct(item) {
    const handle = item.handle;
    if (!handle) return item;

    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to fetch product');

    const data = await response.json();
    const variants = Array.isArray(data.variants) ? data.variants : [];
    const availableVariant = variants.find((variant) => variant && variant.available) || variants[0] || {};
    const cents = Number.isFinite(data.price) ? Number(data.price) : Number(availableVariant.price);
    const images = Array.isArray(data.images) ? data.images : [];
    const image = images.length ? images[0] : item.image;
    const variantImageSrc =
      (availableVariant.featured_image && availableVariant.featured_image.src) ||
      (availableVariant.image && availableVariant.image.src) ||
      '';

    return {
      handle,
      productId: String(data.id ?? item.productId ?? ''),
      title: data.title || item.title,
      url: data.url || item.url || `/products/${handle}`,
      image: normaliseImage(image || item.image),
      price: formatPrice(cents, item.price),
      variantId: availableVariant.id ? String(availableVariant.id) : item.variantId ?? '',
      available: Boolean(availableVariant && availableVariant.available),
      variantImage: normaliseImage(variantImageSrc || image || item.image, 100),
    };
  }

  /**
   * @param {FavoritesItem} product
   * @returns {string}
   */
  createCardMarkup(product) {
    const title = escapeHtml(product.title || product.handle);
    const url = escapeHtml(product.url || `/products/${product.handle}`);
    const price = escapeHtml(product.price || '');
    const image = escapeHtml(product.image);
    const sendLabel = escapeHtml(this.templates.send || this.templates.view);
    const buttonMarkup = this.createToggleMarkup(product, 'favorite-toggle--card');
    const sendGiftMarkup = this.createSendGiftMarkup(product, sendLabel);

    return `
      <article class="favorites-page__card" data-favorite-item="${escapeHtml(product.handle)}">
        <div class="favorites-page__card-image">
          ${image ? `<img src="${image}" alt="${title}">` : ''}
          ${buttonMarkup}
        </div>
        <div class="favorites-page__card-body">
          <h3 class="favorites-page__card-title">
            <a href="${url}" class="link">${title}</a>
          </h3>
          ${price ? `<span class="favorites-page__card-price">${price}</span>` : ''}
          <div class="favorites-page__card-actions">
            ${sendGiftMarkup}
          </div>
        </div>
      </article>
    `;
  }

  /**
   * @param {FavoritesItem} product
   * @param {string} sendLabel
   * @returns {string}
   */
  createSendGiftMarkup(product, sendLabel) {
    const isAvailable = Boolean(product.variantId) && product.available === true;
    const soldOutLabel = escapeHtml(this.templates.soldOut || sendLabel);
    const addedLabel = escapeHtml(this.templates.added || '');
    const buttonLabel = isAvailable ? sendLabel : soldOutLabel;
    const variantId = escapeHtml(product.variantId || '');
    const productId = escapeHtml(product.productId || '');
    const sectionId = escapeHtml(this.sectionId || '');
    const quantityDefault = escapeHtml(this.quantityDefault || '1');
    const variantMedia = escapeHtml(product.variantImage || product.image || '');
    const buttonAttributes = [];
    const inputAttributes = [];

    if (!isAvailable) {
      buttonAttributes.push('disabled', 'aria-disabled="true"');
      inputAttributes.push('disabled');
    }

    const iconMarkup = isAvailable
      ? `<span class="svg-wrapper add-to-cart-icon" aria-hidden="true">${ADD_TO_CART_ICON}</span>`
      : '';

    const variantMediaAttribute = variantMedia ? ` data-product-variant-media="${variantMedia}"` : '';

    return `
      <product-form-component
        class="favorites-page__product-form"
        data-section-id="${sectionId}"
        data-product-id="${productId}"
        data-quantity-default="${quantityDefault}"
        data-redirect-to-cart="true"
        on:submit="/handleSubmit"
      >
        <div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>
        <form method="post" action="/cart/add" novalidate data-type="add-to-cart-form" class="favorites-page__form">
          <input type="hidden" name="form_type" value="product">
          <input type="hidden" name="utf8" value="✓">
          <input type="hidden" name="id" value="${variantId}" ref="variantId"${
            inputAttributes.length ? ` ${inputAttributes.join(' ')}` : ''
          }>
          <input type="hidden" name="quantity" value="${quantityDefault}">
          <add-to-cart-component ref="addToCartButtonContainer"${variantMediaAttribute}>
            <button type="submit" name="add" class="button add-to-cart-button button-secondary favorites-page__send-gift" ref="addToCartButton"${
              buttonAttributes.length ? ` ${buttonAttributes.join(' ')}` : ''
            } on:click="/handleClick">
              <span class="add-to-cart-text">
                ${iconMarkup}
                <span class="add-to-cart-text__content">${buttonLabel}</span>
              </span>
              <span aria-hidden="true" class="add-to-cart-text--added">
                <span class="svg-wrapper add-to-cart-icon--added">${CHECKMARK_ICON}</span>
                <span>${addedLabel}</span>
              </span>
            </button>
          </add-to-cart-component>
        </form>
      </product-form-component>
    `;
  }

  /**
   * @param {FavoritesItem} product
   * @param {string} additionalClass
   * @returns {string}
   */
  createToggleMarkup(product, additionalClass = '') {
    const addLabel = escapeHtml(this.getLabel('add', product.title || product.handle));
    const removeLabel = escapeHtml(this.getLabel('remove', product.title || product.handle));
    const loadingLabel = escapeHtml(this.templates.loading);
    const classes = ['favorite-toggle', additionalClass].filter(Boolean).join(' ');
    const image = normaliseImage(product.image);

    return `
      <button
        type="button"
        class="${classes}"
        data-favorite-toggle
        data-product-handle="${escapeHtml(product.handle)}"
        data-product-id="${escapeHtml(product.productId || '')}"
        data-product-url="${escapeHtml(product.url || `/products/${product.handle}`)}"
        data-product-title="${escapeHtml(product.title || product.handle)}"
        data-product-price="${escapeHtml(product.price || '')}"
        ${image ? `data-product-image="${escapeHtml(image)}"` : ''}
        data-label-add="${addLabel}"
        data-label-remove="${removeLabel}"
        data-loading-label="${loadingLabel}"
        aria-pressed="false"
        aria-label="${addLabel}"
      >
        <span class="favorite-toggle__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" role="presentation" focusable="false">
            <path d="M12 21.35 10.55 20.03C5.4 15.36 2 12.27 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.77-3.4 6.86-8.55 11.54z"></path>
          </svg>
        </span>
        <span class="favorite-toggle__label visually-hidden">${addLabel}</span>
      </button>
    `;
  }

  /**
   * @param {'add' | 'remove'} type
   * @param {string} title
   * @returns {string}
   */
  getLabel(type, title) {
    const template = this.templates[type] || '';
    return template.replace(/\{\{\s*product_title\s*\}\}/g, title);
  }

  /**
   * Redirect to the cart when a favourites add-to-cart completes successfully.
   * @param {Event & {target: HTMLElement, detail?: {data?: {didError?: boolean}}}} event
   */
  handleCartUpdate(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.dataset.redirectToCart !== 'true') return;
    const didError = event.detail?.data?.didError;
    if (didError) return;
    const destination = window?.Theme?.routes?.cart_url || '/cart';
    window.location.href = destination;
  }

  ensurePageStyles() {
    if (this.pageStylesLoaded) return;

    const href = window.themeStyles?.favoritesPage;
    if (!href) return;

    const existing = document.querySelector('link[data-favorites-page-styles]');
    if (existing) {
      this.pageStylesLoaded = true;
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.media = 'all';
    link.dataset.favoritesPageStyles = 'true';
    link.addEventListener('load', () => {
      this.pageStylesLoaded = true;
    });
    document.head.append(link);
  }
}

/**
 * @typedef {Object} FavoritesItem
 * @property {string} handle
 * @property {string} [productId]
 * @property {string} [title]
 * @property {string} [url]
 * @property {string} [image]
 * @property {string} [price]
 * @property {string} [variantId]
 * @property {boolean} [available]
 * @property {string} [variantImage]
 */

onDocumentReady(() => {
  window.t2mdFavorites = new FavoritesManager();
});
